import {
	Paper, Stack, Title, Button, Group, FileInput, Image, Anchor
} from '@mantine/core';
import Badge, { BADGE_LIGHT_STYLES } from '@ui/components/Badge/Badge';
import Icon from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';
import Workspace from 'src/entities/workspace';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import FeaturePaywall from '@workspaces/components/FeaturePaywall/FeaturePaywall';
import useEditWorkspaceLogo from './useEditWorkspaceLogo';

const LogoPositionItem = ( { label, color }: { label: string; color?: string; } ) => (
	<Group spacing={8}>
		<Icon icon="check_circle_outline" size={18} color={color} />
		<Text color={color} variant="body">{label}</Text>
	</Group>
);

const EditWorkspaceLogo = ( {
	workspace, readOnly
}: { workspace: Workspace; readOnly: boolean; } ) => {
	const {
		placeholder,
		onFileChange,
		logo,
		preview,
		saveButtonDisabled,
		fileInputDisabled,
		onSaveClick,
		isSaving,
		onRestoreDefaultClick,
		restoreDefaultButtonDisabled,
		isDeletingLogo,
		isFeatureAvailable,
		onCancelClick,
		showCancelButton
	} = useEditWorkspaceLogo( { workspace, readOnly } );

	const titleColor = isFeatureAvailable ? 'var(--color-text-primary)' : 'var(--color-text-light)';
	const bodyColor = isFeatureAvailable ? undefined : 'var(--color-text-light)';

	return (
		<Paper p={20} shadow="xs" style={{ background: 'var(--color-bg-container)' }}>
			<Stack spacing={20} align="stretch">
				<Stack spacing={14}>
					<Group spacing={14}>
						<Title color={titleColor} order={4}>Workspace Logo</Title>
						<Anchor
							h="18px"
							href="https://support.verybusy.io/en/articles/12710113-custom-branding-for-workspaces"
							target="_blank"
						>
							<Icon icon="info_outlined" size={18} color="var(--color-text-primary)" />
						</Anchor>
						<FeaturePaywall
							feature={PricingPlanFeature.WORKSPACE_CUSTOM_BRANDING}
							workspace={workspace}
							origin="editWorkspaceLogo"
						>
							{( { planName } ) => (
								<Badge
									color="green"
									size="xs"
									variant="light"
									styles={{
										...BADGE_LIGHT_STYLES,
										root: {
											...BADGE_LIGHT_STYLES.root,
											backgroundColor: 'var(--color-feedback-on-success)',
											color: 'var(--color-text-primary)'
										}
									}}
								>
									{`${planName} Only`}
								</Badge>
							)}
						</FeaturePaywall>
					</Group>
					<Text color={bodyColor}>For best contrast, use a white version.</Text>
				</Stack>
				<FileInput
					icon={<Icon icon="upload" size={18} />}
					value={logo}
					onChange={onFileChange}
					accept="image/png,image/svg+xml"
					disabled={fileInputDisabled}
					placeholder={placeholder}
				/>
				{( preview ) && (
					<Stack h={72} align="center" justify="center" bg="blue.9" style={{ borderRadius: '4px' }}>
						<Image src={preview} alt="Workspace Logo" width="213px" height="49px" fit="contain" />
					</Stack>
				)}
				<Stack>
					<Text color={titleColor} variant="body-bold">Where will this logo be applied?</Text>
					<Group spacing={40}>
						<Stack spacing={10}>
							<LogoPositionItem label="Project View" color={bodyColor} />
							<LogoPositionItem label="Share Link Sign-Up Page" color={bodyColor} />
						</Stack>
						<Stack spacing={10}>
							<LogoPositionItem label="Project Invite Sign-Up Page" color={bodyColor} />
							<LogoPositionItem label="Project Email Notification" color={bodyColor} />
						</Stack>
					</Group>
				</Stack>
				<Group spacing={6} position="right">
					{showCancelButton ? (
						<Button
							variant="default"
							onClick={onCancelClick}
						>
							Cancel
						</Button>
					) : (
						<Button
							leftIcon={<Icon icon="settings_backup_restore" size={18} color="var(--color-text-primary)" />}
							variant="default"
							onClick={onRestoreDefaultClick}
							loading={isDeletingLogo}
							disabled={restoreDefaultButtonDisabled}
						>
							Restore Default
						</Button>
					)}
					<Button disabled={saveButtonDisabled} onClick={onSaveClick} loading={isSaving}>
						Save
					</Button>
				</Group>
			</Stack>
		</Paper>
	);
};

export default EditWorkspaceLogo;
